// src/HospedeModal.js

import React, { useState, useEffect } from 'react';
import { db } from './firebaseConfig';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import {
  Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Button, Grid, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';

// Máscaras manuais
const maskCPF = (value) => {
  return value
    .replace(/\D/g, '')
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2')
    .slice(0, 14);
};

const maskCelular = (value) => {
  return value
    .replace(/\D/g, '')
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .slice(0, 15);
};

const HospedeModal = ({ open, onClose, onSave, hospedeParaEditar }) => {
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [celular, setCelular] = useState('');
  const [email, setEmail] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('');
  const [tipoHospede, setTipoHospede] = useState('AABB_ARACAJU');
  const [qualAABB, setQualAABB] = useState('');
  const [indicadoPor, setIndicadoPor] = useState('');
  const [observacao, setObservacao] = useState('');
  const [matricula, setMatricula] = useState('');

  const [erros, setErros] = useState({});

  useEffect(() => {
    if (hospedeParaEditar) {
      setNome(hospedeParaEditar.nome || '');
      setCpf(hospedeParaEditar.cpf || '');
      setCelular(hospedeParaEditar.celular || '');
      setEmail(hospedeParaEditar.email || '');
      setCidade(hospedeParaEditar.cidade || '');
      setEstado(hospedeParaEditar.estado || '');
      setTipoHospede(hospedeParaEditar.tipoHospede || 'AABB_ARACAJU');
      setQualAABB(hospedeParaEditar.qualAABB || '');
      setIndicadoPor(hospedeParaEditar.indicadoPor || '');
      setObservacao(hospedeParaEditar.observacao || '');
      setMatricula(hospedeParaEditar.matricula || '');
      setErros({});
    } else {
      setNome(''); setCpf(''); setCelular(''); setEmail('');
      setCidade(''); setEstado(''); setTipoHospede('AABB_ARACAJU');
      setQualAABB(''); setIndicadoPor(''); setObservacao(''); setMatricula('');
      setErros({});
    }
  }, [hospedeParaEditar, open]);

  const handleSubmit = async () => {
    const novosErros = {};
    if (!nome.trim()) novosErros.nome = "Nome é obrigatório";
    if (!cpf.trim()) novosErros.cpf = "CPF é obrigatório";
    if (!celular.trim()) novosErros.celular = "Celular é obrigatório";
    if (tipoHospede === 'AABB_ARACAJU' && !matricula.trim()) {
      novosErros.matricula = "Matrícula é obrigatória";
    }

    setErros(novosErros);

    if (Object.keys(novosErros).length > 0) {
      toast.error("Preencha os campos obrigatórios.");
      return;
    }

    const dadosHospede = {
      nome: nome.toUpperCase(),
      cpf,
      celular,
      email,
      cidade,
      estado,
      tipoHospede,
      matricula: tipoHospede === 'AABB_ARACAJU' ? matricula : '',
      qualAABB: tipoHospede === 'OUTRA_AABB' ? qualAABB : '',
      indicadoPor: tipoHospede === 'NAO_ASSOCIADO' ? indicadoPor : '',
      observacao,
      dataCadastro: hospedeParaEditar?.dataCadastro || new Date(),
    };

    try {
      let hospedeSalvo;
      if (hospedeParaEditar && hospedeParaEditar.id) {
        const hospedeRef = doc(db, 'hospedes', hospedeParaEditar.id);
        await updateDoc(hospedeRef, dadosHospede);
        hospedeSalvo = { id: hospedeParaEditar.id, ...dadosHospede };
        toast.success('Hóspede atualizado com sucesso!');
      } else {
        const docRef = await addDoc(collection(db, 'hospedes'), dadosHospede);
        hospedeSalvo = { id: docRef.id, ...dadosHospede };
        toast.success('Hóspede cadastrado com sucesso!');
      }
      onSave(hospedeSalvo);
      onClose();
    } catch (error) {
      toast.error('Erro ao salvar hóspede.');
      console.error("Erro ao salvar hóspede:", error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{hospedeParaEditar && hospedeParaEditar.id ? 'Editar Hóspede' : 'Novo Hóspede'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Nome Completo"
              value={nome}
              onChange={e => setNome(e.target.value)}
              fullWidth
              required
              error={!!erros.nome}
              helperText={erros.nome}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="CPF"
              value={cpf}
              onChange={e => setCpf(maskCPF(e.target.value))}
              fullWidth
              required
              error={!!erros.cpf}
              helperText={erros.cpf}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Celular"
              value={celular}
              onChange={e => setCelular(maskCelular(e.target.value))}
              fullWidth
              required
              error={!!erros.celular}
              helperText={erros.celular}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Cidade"
              value={cidade}
              onChange={e => setCidade(e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Estado (UF)"
              value={estado}
              onChange={e => setEstado(e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <InputLabel></InputLabel>
            <FormControl fullWidth>
              <Select value={tipoHospede} onChange={e => setTipoHospede(e.target.value)}>
                <MenuItem value="AABB_ARACAJU">Associado AABB Aracaju</MenuItem>
                <MenuItem value="OUTRA_AABB">Associado Outra AABB</MenuItem>
                <MenuItem value="NAO_ASSOCIADO">Não Associado</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {tipoHospede === 'AABB_ARACAJU' && (
            <Grid item xs={12}>
              <TextField
                label="Qual a matrícula?"
                value={matricula}
                onChange={e => setMatricula(e.target.value)}
                fullWidth
                required
                error={!!erros.matricula}
                helperText={erros.matricula}
              />
            </Grid>
          )}
          {tipoHospede === 'OUTRA_AABB' && (
            <Grid item xs={12}>
              <TextField
                label="Qual AABB?"
                value={qualAABB}
                onChange={e => setQualAABB(e.target.value)}
                fullWidth
              />
            </Grid>
          )}
          {tipoHospede === 'NAO_ASSOCIADO' && (
            <Grid item xs={12}>
              <TextField
                label="Indicado Por"
                value={indicadoPor}
                onChange={e => setIndicadoPor(e.target.value)}
                fullWidth
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label="Observações"
              value={observacao}
              onChange={e => setObservacao(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained">
          {hospedeParaEditar && hospedeParaEditar.id ? 'Salvar Alterações' : 'Salvar Hóspede'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HospedeModal;
